import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CrmPage from "./CrmPage";
import { crmFetch, TEMPLATE_CHANNELS } from "./crmApi";
import "./Crm.css";

const STARTERS = [
  {
    name: "First reply to an enquiry",
    channel: "whatsapp",
    body: "Hi {{client_name}}! Thanks for reaching out about your {{event_type}} on {{event_date}}. We'd love to be part of it — may I send across our packages?",
  },
  {
    name: "Sending the quote",
    channel: "whatsapp",
    body: "Hi {{client_name}}, here's your quote from {{studio_name}}: {{quote_link}}\n\nTotal: {{quote_total}}. Happy to adjust anything — just let me know!",
  },
  {
    name: "Gentle follow-up",
    channel: "whatsapp",
    body: "Hi {{client_name}}, just checking in on the quote we sent for your {{event_type}}. Any questions? Happy to hold the date for a couple more days.",
  },
];

/**
 * Message templates for the repetitive parts of chasing work. Picked from the
 * lead drawer, where placeholders get filled from that lead.
 */
function CrmTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planBlocked, setPlanBlocked] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", channel: "whatsapp", body: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await crmFetch("/api/crm/templates");
      setTemplates(d.templates || []);
      setPlaceholders(d.placeholders || []);
    } catch (e) {
      if (e.status === 401) navigate("/login");
      if (e.status === 403 && e.data?.code === "upgrade_required") setPlanBlocked(true);
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const openNew = (starter) => {
    setForm(starter
      ? { name: starter.name, channel: starter.channel, body: starter.body }
      : { name: "", channel: "whatsapp", body: "" });
    setError("");
    setEditing({});
  };

  const openEdit = (tpl) => {
    setForm({ name: tpl.name, channel: tpl.channel, body: tpl.body });
    setError("");
    setEditing(tpl);
  };

  const insertPlaceholder = (key) =>
    setForm((f) => ({ ...f, body: `${f.body}${f.body && !f.body.endsWith(" ") ? " " : ""}${key}` }));

  const save = async () => {
    if (!form.name.trim() || !form.body.trim()) {
      setError("A template needs a name and a message.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editing.id) {
        await crmFetch(`/api/crm/templates/${editing.id}`, { method: "PUT", body: form });
      } else {
        await crmFetch("/api/crm/templates", { method: "POST", body: form });
      }
      setEditing(null);
      load();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const remove = async (tpl) => {
    if (!window.confirm(`Delete the "${tpl.name}" template?`)) return;
    try {
      await crmFetch(`/api/crm/templates/${tpl.id}`, { method: "DELETE" });
      load();
    } catch (e) {
      window.alert(e.message);
    }
  };

  const channelInfo = (key) =>
    TEMPLATE_CHANNELS.find((c) => c.key === key) || TEMPLATE_CHANNELS[0];

  return (
    <CrmPage
      title="💬 Templates"
      subtitle="Write the message once, send it in two taps from any lead"
      loading={loading}
      planBlocked={planBlocked}
      actions={<button className="crm-primary-btn" onClick={() => openNew(null)}>＋ New Template</button>}
    >
      {templates.length === 0 ? (
        <div className="crm-empty">
          <span className="crm-empty-icon">💬</span>
          No templates yet.
          <div className="crm-empty-hint">
            Start from one of these — you can edit every word before saving.
          </div>
          <div className="crm-starter-row">
            {STARTERS.map((s) => (
              <button key={s.name} className="crm-btn ghost" onClick={() => openNew(s)}>
                ＋ {s.name}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="crm-pkg-grid">
          {templates.map((t) => {
            const ch = channelInfo(t.channel);
            return (
              <div key={t.id} className="crm-card crm-pkg-card">
                <div className="crm-pkg-card-head">
                  <div>
                    <h3>{t.name}</h3>
                    <p className="crm-row-sub">{ch.icon} {ch.label}</p>
                  </div>
                </div>
                <pre className="crm-tpl-body">{t.body}</pre>
                <div className="crm-quote-card-actions">
                  <button className="crm-btn ghost" onClick={() => openEdit(t)}>✏️ Edit</button>
                  <button className="crm-btn danger" onClick={() => remove(t)}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing !== null && (
        <div className="crm-modal-backdrop" onClick={() => setEditing(null)}>
          <div className="crm-modal crm-quote-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editing.id ? "Edit template" : "＋ New template"}</h3>
            {error && <div className="crm-form-error">{error}</div>}
            <div className="crm-form-grid">
              <div className="crm-field">
                <label>Template name</label>
                <input
                  value={form.name}
                  autoFocus
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Gentle follow-up"
                />
              </div>
              <div className="crm-field">
                <label>Channel</label>
                <select
                  value={form.channel}
                  onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}
                >
                  {TEMPLATE_CHANNELS.map((c) => (
                    <option key={c.key} value={c.key}>{c.icon} {c.label}</option>
                  ))}
                </select>
              </div>
              <div className="crm-field full">
                <label>Message</label>
                <textarea
                  style={{ minHeight: 130 }}
                  value={form.body}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  placeholder="Hi {{client_name}}, …"
                />
              </div>
            </div>

            <div className="crm-ph-row">
              <span className="crm-pkg-label">Insert:</span>
              {placeholders.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className="crm-pkg-chip"
                  title={p.desc}
                  onClick={() => insertPlaceholder(p.key)}
                >
                  {p.key}
                </button>
              ))}
            </div>

            <div className="crm-modal-actions">
              <button className="crm-btn ghost" onClick={() => setEditing(null)}>Cancel</button>
              <button className="crm-btn primary" disabled={saving} onClick={save}>
                {saving ? "Saving…" : editing.id ? "Save changes" : "Create template"}
              </button>
            </div>
          </div>
        </div>
      )}
    </CrmPage>
  );
}

export default CrmTemplates;
